/**
 * Das Bundle einlesen: `logbook.json.gz` -> IndexedDB.
 *
 * Die App enthaelt selbst keine Daten (Architekturgrundsatz). Der Nutzer waehlt
 * die Datei einmal aus, danach lebt alles lokal im Browser.
 *
 * Entpackt wird mit `DecompressionStream` — im Browser eingebaut, keine
 * Bibliothek noetig. Die Messpunkte wandern getrennt von den Kopfdaten in die
 * Datenbank, damit die Liste spaeter nicht eine Million Zahlen anfassen muss.
 */

import { SPEICHER, alleAus, alleTauchgaenge, schreibe, setzeMeta } from './db.js';
import { grabsteine, istGeloescht, speichere } from './saetze.js';

export const FORMAT = 'tauchlogbuch/1';

/** gzip erkennt man an den ersten zwei Bytes, nicht am Dateinamen. */
export async function istGezippt(datei) {
  const kopf = new Uint8Array(await datei.slice(0, 2).arrayBuffer());
  return kopf[0] === 0x1f && kopf[1] === 0x8b;
}

/**
 * Entpackt das Bundle, wenn noetig.
 *
 * Geprueft wird der Inhalt, nicht die Endung: Eine umbenannte Datei kommt
 * genauso vor wie ein Server, der `.gz` bereits mit `Content-Encoding` entpackt
 * ausliefert. Beides darf nicht in einer raetselhaften Fehlermeldung enden.
 */
export async function entpacke(datei) {
  if (!(await istGezippt(datei))) return datei.text();
  if (typeof DecompressionStream !== 'function') {
    throw new Error('Dieser Browser kann keine .gz-Dateien entpacken. '
      + 'Nimm das Bundle unkomprimiert oder einen neueren Browser.');
  }
  const strom = datei.stream().pipeThrough(new DecompressionStream('gzip'));
  return new Response(strom).text();
}

export function pruefeBundle(daten) {
  if (!daten || typeof daten !== 'object') throw new Error('Die Datei enthält kein Bundle.');
  if (daten.format !== FORMAT) {
    throw new Error(`Unbekanntes Format: ${daten.format ?? 'keines'}. Erwartet wird ${FORMAT}.`);
  }
  if (!Array.isArray(daten.dives)) throw new Error('Im Bundle fehlen die Tauchgänge.');
  return daten;
}

/**
 * Was ein Import täte, ohne ihn zu tun.
 *
 * Getrennt von der Ausführung, damit die Oberfläche das Ergebnis zeigen kann,
 * bevor irgendetwas geschrieben wird — bei 995 Tauchgängen will man vorher
 * wissen, was ersetzt würde.
 */
export function planeImport(ausBundle, lokal, merkzettel, modus = 'ergaenzen') {
  const nachId = new Map(lokal.map((satz) => [satz.id, satz]));
  const plan = { neu: [], ersetzt: [], unveraendert: [], uebersprungen: [] };

  for (const satz of ausBundle) {
    if (istGeloescht(merkzettel, satz.id)) {
      plan.uebersprungen.push(satz);
      continue;
    }
    const eigener = nachId.get(satz.id);
    if (!eigener) {
      plan.neu.push(satz);
    } else if (modus === 'abgleichen'
        && String(satz.geaendert ?? '') > String(eigener.geaendert ?? '')) {
      plan.ersetzt.push(satz);
    } else {
      plan.unveraendert.push(eigener);
    }
  }
  return plan;
}

/**
 * Trennt Kopfdaten von Messpunkten.
 * Zurueck kommen zwei Listen — genau so liegen sie spaeter in der Datenbank.
 */
export function trenne(dives) {
  const kopfdaten = [];
  const profile = [];
  for (const dive of dives) {
    const { samples, ...kopf } = dive;
    kopfdaten.push(kopf);
    if (Array.isArray(samples) && samples.length > 0) {
      profile.push({ dive_id: dive.id, werte: samples });
    }
  }
  return { kopfdaten, profile };
}

/**
 * Das Bundle übernehmen — ergänzend oder abgleichend.
 *
 * Ersetzt wird nichts mehr: Sobald in der App erfasst wird, würde ein
 * ersetzender Import genau diese Arbeit vernichten. `melde` bekommt
 * Zwischenstände für die Fortschrittsanzeige — ein Import von 995 Tauchgängen
 * dauert einige Sekunden, und eine stumme Oberfläche sieht wie ein Absturz aus.
 */
export async function uebernimm(db, daten, melde = () => {}, optionen = {}) {
  pruefeBundle(daten);
  const modus = optionen.modus ?? 'ergaenzen';
  const geraet = optionen.geraet ?? null;

  // Ein Abzug bringt seine Grabsteine mit. Sie müssen vor dem Plan im
  // Speicher liegen, sonst legt derselbe Aufruf gelöschte Sätze wieder an.
  if (Array.isArray(daten.grabsteine) && daten.grabsteine.length > 0) {
    const t = db.transaction(SPEICHER.grabsteine, 'readwrite');
    const store = t.objectStore(SPEICHER.grabsteine);
    for (const g of daten.grabsteine) store.put(g);
    await new Promise((erfuellen, ablehnen) => {
      t.oncomplete = () => erfuellen();
      t.onerror = () => ablehnen(t.error);
    });
  }

  melde('Bestand wird verglichen …');
  const merkzettel = await grabsteine(db);
  const { kopfdaten, profile } = trenne(daten.dives);
  const plan = planeImport(kopfdaten, await alleTauchgaenge(db), merkzettel, modus);
  const zuSchreiben = [...plan.neu, ...plan.ersetzt];

  melde('Tauchgänge werden gespeichert …');
  await speichere(db, SPEICHER.dives, zuSchreiben, { geraet,
    melde: (fertig, gesamt) => melde(`Tauchgänge: ${fertig} von ${gesamt}`) });

  melde('Tauchplätze und Stammdaten …');
  for (const [speicher, liste] of [[SPEICHER.sites, daten.sites], [SPEICHER.trips, daten.trips],
    [SPEICHER.buddies, daten.buddies], [SPEICHER.gear, daten.gear]]) {
    const vorhanden = await alleAus(db, speicher);
    const teilplan = planeImport(liste ?? [], vorhanden, merkzettel, modus);
    await speichere(db, speicher, [...teilplan.neu, ...teilplan.ersetzt], { geraet });
  }

  // Profile folgen ihrem Tauchgang: Nur was geschrieben wurde, bekommt eines.
  const geschrieben = new Set(zuSchreiben.map((s) => s.id));
  const zuProfilen = profile.filter((p) => geschrieben.has(p.dive_id));
  melde('Tiefenprofile …');
  await schreibe(db, SPEICHER.samples, zuProfilen,
    (fertig, gesamt) => melde(`Profile: ${fertig} von ${gesamt}`));

  const messpunkte = zuProfilen.reduce((summe, p) => summe + p.werte.length, 0);
  await setzeMeta(db, 'stand', {
    format: daten.format,
    erzeugt: daten.erzeugt ?? null,
    sample_felder: daten.sample_felder ?? [],
    tauchgaenge: zuSchreiben.length,
    profile: zuProfilen.length,
    messpunkte,
    modus,
    geladen_am: new Date().toISOString(),
  });
  return { tauchgaenge: zuSchreiben.length, profile: zuProfilen.length, messpunkte, plan };
}

/** Datei -> Datenbank, in einem Schritt. */
export async function ausDatei(db, datei, melde = () => {}) {
  melde('Datei wird entpackt …');
  const text = await entpacke(datei);
  melde('Bundle wird gelesen …');
  const daten = JSON.parse(text);
  return uebernimm(db, daten, melde);
}
